// Prüfwarteschlange (GET /review/queue und Entscheidungen) plus Freigabe von Aufmaßen (Vertrag).
import { infiniteQueryOptions } from '@tanstack/react-query'
import { api, ApiError, unwrap, type Schemas } from '@/api/client'

export type ReviewItem = Schemas['ReviewItem']
export type ReviewKind = Schemas['ReviewKind']
export type ReviewFlag = Schemas['ReviewFlag']

export const REVIEW_KINDS: readonly ReviewKind[] = ['VISIT', 'PHOTO', 'MEASUREMENT']
export const REVIEW_FLAGS: readonly ReviewFlag[] = [
  'backdated',
  'photo_time_implausible',
  'open_too_long',
  'off_site',
  'no_fix',
  'clock_skew',
  'capped',
  'joined_later',
  'departure_missing',
  'rejected',
  'hash_mismatch',
  'malware',
  'measurement_submitted',
]

export interface ReviewFilters {
  kind?: ReviewKind
  flag?: ReviewFlag
  rajon?: string
}

export const reviewKeys = { queue: ['review', 'queue'] as const }

export function reviewQueueQuery(filters: ReviewFilters) {
  return infiniteQueryOptions({
    queryKey: [...reviewKeys.queue, filters] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/review/queue', {
          params: {
            query: {
              ...(filters.kind ? { kind: [filters.kind] } : {}),
              ...(filters.flag ? { flag: filters.flag } : {}),
              ...(filters.rajon ? { rajon: filters.rajon } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
              limit: 50,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
}

const idempotency = () => ({ 'Idempotency-Key': crypto.randomUUID() })

export async function decideVisit(id: string, decision: 'ACCEPT' | 'REJECT', reason?: string) {
  return unwrap(
    await api.POST('/api/v1/visits/{id}/review', {
      params: { path: { id }, header: idempotency() },
      body: { decision, ...(reason ? { reason } : {}) },
    }),
  )
}

export async function decideFile(
  id: string,
  decision: 'ACCEPT' | 'REQUEST_RETAKE',
  reason?: string,
) {
  const result = await api.POST('/api/v1/files/{id}/review', {
    params: { path: { id }, header: idempotency() },
    body: { decision, ...(reason ? { reason } : {}) },
  })
  if (!result.response.ok) throw new ApiError(result.response.status, result.error as never)
}

export async function returnMeasurement(id: string, reason: string) {
  return unwrap(
    await api.POST('/api/v1/measurements/{id}/return', {
      params: { path: { id }, header: idempotency() },
      body: { reason },
    }),
  )
}

/** Freigabe über den Vertrag (POST /measurements/{id}/approve). */
export async function approveMeasurement(id: string) {
  return unwrap(await api.POST('/api/v1/measurements/{id}/approve', { params: { path: { id } } }))
}
